/**
 * Hook para WebSocket de mensajes de WhatsApp
 * Conecta con el microservicio wa-messages-api
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const useWhatsAppMessages = (onNewMessage, onBotStatus, onMessageReaction) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeout = useRef(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 10;

  const connect = useCallback(() => {
    try {
      // Determinar URL del WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/ws/conversations`;

      console.log('🔌 Conectando WebSocket WhatsApp:', wsUrl);

      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('✅ WebSocket WhatsApp conectado');
        setIsConnected(true);
        setReconnectAttempts(0);

        // Ping cada 30 segundos
        const pingInterval = setInterval(() => {
          if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        ws.current.pingInterval = pingInterval;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'pong' || data.type === 'connected') {
            return;
          }

          console.log('📨 WS Message:', data.type);

          if (data.type === 'new_message' && onNewMessage) {
            onNewMessage(data.data);
          }
          
          if (data.type === 'message_sent' && onNewMessage) {
            onNewMessage(data.data);
          }

          if (data.type === 'message_reaction' && onMessageReaction) {
            onMessageReaction(data.data);
          }

          if (data.type === 'bot_status' && onBotStatus) {
            onBotStatus(data.data);
          }
        } catch (error) {
          console.error('Error parseando mensaje WS:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('❌ Error WebSocket:', error);
      };

      ws.current.onclose = () => {
        console.log('❌ WebSocket desconectado');
        setIsConnected(false);

        if (ws.current && ws.current.pingInterval) {
          clearInterval(ws.current.pingInterval);
        }

        // Reconectar con backoff exponencial
        if (reconnectAttempts < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          console.log(`🔄 Reconectando en ${delay / 1000}s...`);
          
          reconnectTimeout.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('Error creando WebSocket:', error);
    }
  }, [onNewMessage, onBotStatus, onMessageReaction, reconnectAttempts]);

  useEffect(() => {
    connect();

    return () => {
      console.log('🔌 Desconectando WebSocket WhatsApp');
      
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }

      if (ws.current) {
        if (ws.current.pingInterval) {
          clearInterval(ws.current.pingInterval);
        }
        ws.current.close();
      }
    };
  }, []);

  return { isConnected, reconnect: connect };
};

export default useWhatsAppMessages;
